import { spawn } from 'child_process';
import { promises as fs } from 'fs';
import os from 'os';
import path from 'path';
import type { GitWorkspace, PromptExecutionWorkspace } from '@whittlelabs/loom';

/**
 * Materialized-checkout support for filesystem-capable executors. The
 * `PromptExecutionWorkspace` bag is namespaced by technology; this executor
 * consumes the `git` namespace only — a workspace carrying no namespace it
 * understands degrades rather than running somewhere wrong. Each job gets a
 * FRESH, throwaway checkout at exactly the requested commit under a temp
 * root, removed when the job settles: a self-contained init + shallow fetch +
 * detached checkout, sharing nothing with any long-lived clone on the host.
 * (An earlier design reused a configured clone's object store for fetch
 * speed; it silently poured every reviewed repo's objects into whatever repo
 * the configured directory happened to belong to — dropped 2026-07-13.)
 *
 * The commit is the integrity anchor: the checkout is only created once
 * `git cat-file` proves the exact commit exists locally. A fetch that lands
 * the wrong objects can therefore never produce a wrong checkout — only a
 * degrade. Any forge knowledge (what a pull request is) lives in the
 * producer's choice of `fetchRefs`; this module knows only git.
 *
 * Degrade path: when the commit cannot be materialized (offline, force-pushed
 * head), the executor runs in an EMPTY scratch directory. Exploring a stale
 * checkout produces confidently wrong citations, which is worse than
 * prompt-only review; the prompt tells the model to fall back to the diff
 * when the filesystem is empty.
 */
export interface PreparedWorkspace {
  /** Directory the executor should run the model in. */
  cwd: string;
  /** True when `cwd` is a checkout at exactly the requested head SHA. */
  materialized: boolean;
  /** Present when materialization failed and `cwd` is an empty scratch dir. */
  warning?: string;
  /** Remove the worktree/scratch dir. Call exactly once, after the child settles. Never throws. */
  cleanup(): Promise<void>;
}

export interface PrepareWorkspaceOptions {
  signal?: AbortSignal;
  /**
   * Base directory the throwaway checkout is created under (via mkdtemp).
   * Defaults to the OS temp dir; the sifter exposes it as `--tmp-dir` for
   * hosts whose default temp is small, slow, or on the wrong volume.
   */
  tmpDir?: string;
  /** Per-git-command budget; a hung fetch must not eat the job deadline. */
  commandTimeoutMs?: number;
  /**
   * Git transport allowlist (`GIT_ALLOW_PROTOCOL` form). Defaults to the
   * transports real producers use — `remote` is always an `https://` URL
   * from Sift, and the profile clone fetches its `origin` over https/ssh.
   * `file:` is deliberately EXCLUDED by default: no production path needs
   * it, and on a shared host (hosted tier) it would let a workspace spec
   * point `remote` at an arbitrary local path. Tests using local-path
   * fixtures opt back in.
   */
  allowedProtocols?: string;
  /**
   * Short-lived credential (e.g. a GitHub OAuth token) for fetching from a
   * private `remote`. Injected as an `http.<origin>.extraheader` via
   * `GIT_CONFIG_*` env — never argv (which is world-readable in /proc),
   * never logged. Ignored unless `remote` is an https URL. Omit for public
   * sources; a public fetch needs no token.
   */
  gitToken?: string;
}

const DEFAULT_GIT_TIMEOUT_MS = 120_000;
const DEFAULT_GIT_PROTOCOLS = 'https:ssh:git';

export async function prepareWorkspace(
  workspace: PromptExecutionWorkspace,
  options: PrepareWorkspaceOptions = {},
): Promise<PreparedWorkspace> {
  const tmpBase = options.tmpDir ?? os.tmpdir();
  const resolved = readGitWorkspace(workspace);
  if ('warning' in resolved) {
    return degradedWorkspace(resolved.warning, tmpBase);
  }
  const gitWorkspace = resolved.git;

  const timeoutMs = options.commandTimeoutMs ?? DEFAULT_GIT_TIMEOUT_MS;
  const allowedProtocols = options.allowedProtocols ?? DEFAULT_GIT_PROTOCOLS;
  const signal = options.signal;
  // Private-fetch auth rides the git child's env as an http extraheader —
  // never argv (/proc-visible), never logged. Undefined for public fetches
  // and non-https remotes.
  const authEnv =
    options.gitToken !== undefined
      ? buildGitAuthEnv(gitWorkspace.remote, options.gitToken)
      : undefined;
  const git = (args: string[], cwd: string) =>
    runGit(args, cwd, { timeoutMs, signal, allowedProtocols, extraEnv: authEnv });

  const hasCommit = (dir: string): Promise<boolean> =>
    git(['cat-file', '-e', `${gitWorkspace.commit}^{commit}`], dir).then((r) => r.code === 0);

  // Fetch the commit into the fresh checkout, trying the cheapest source
  // first. `--depth=1` keeps the fetch small — exploration reads files at one
  // commit, not history.
  const fetchCommit = async (
    dir: string,
    opts: { shallow: boolean },
  ): Promise<string[]> => {
    if (await hasCommit(dir)) return [];
    const depth = opts.shallow ? ['--depth=1'] : [];
    const attempts: Array<{ source: string; args: string[] }> = [];
    for (const ref of gitWorkspace.fetchRefs ?? []) {
      attempts.push({
        source: `${gitWorkspace.remote} ${ref}`,
        args: ['fetch', '--no-tags', ...depth, gitWorkspace.remote, ref],
      });
    }
    attempts.push({
      source: `${gitWorkspace.remote} commit`,
      args: ['fetch', '--no-tags', ...depth, gitWorkspace.remote, gitWorkspace.commit],
    });

    const failures: string[] = [];
    for (const { source, args } of attempts) {
      const result = await git(args, dir);
      if (result.code !== 0) {
        failures.push(`${source}: ${firstLine(result.stderr)}`);
        continue;
      }
      if (await hasCommit(dir)) return [];
      failures.push(`${source}: fetch succeeded but ${gitWorkspace.commit} is still absent`);
    }
    return failures;
  };

  const notMaterialized = (failures: string[]): string =>
    `could not materialize ${gitWorkspace.commit} from ${gitWorkspace.remote}` +
    (failures.length > 0 ? ` (${failures.join('; ')})` : '');

  // Every job is self-contained: init a throwaway repo in a fresh temp dir,
  // shallow-fetch the commit, check it out in place. The whole dir is removed
  // on cleanup, and no host repo is ever touched (`includeOrigin: false`).
  const tempRoot = await fs.mkdtemp(path.join(tmpBase, 'shuttle-ws-'));
  const checkoutDir = path.join(tempRoot, 'checkout');
  const removeTempRoot = async () => {
    await fs.rm(tempRoot, { recursive: true, force: true }).catch(() => {});
  };
  await fs.mkdir(checkoutDir);

  const init = await git(['init', '-q'], checkoutDir);
  if (init.code !== 0) {
    await removeTempRoot();
    return degradedWorkspace(`git init failed: ${firstLine(init.stderr)}`, tmpBase);
  }
  const failures = await fetchCommit(checkoutDir, { shallow: true });
  if (!(await hasCommit(checkoutDir))) {
    await removeTempRoot();
    return degradedWorkspace(notMaterialized(failures), tmpBase);
  }
  const checkout = await git(['checkout', '--detach', gitWorkspace.commit], checkoutDir);
  if (checkout.code !== 0) {
    await removeTempRoot();
    return degradedWorkspace(`git checkout failed: ${firstLine(checkout.stderr)}`, tmpBase);
  }
  return { cwd: checkoutDir, materialized: true, cleanup: removeTempRoot };
}

/**
 * Build the `GIT_CONFIG_*` env that injects a bearer credential as an
 * `http.<origin>.extraheader` (the exact mechanism GitHub's actions/checkout
 * uses). Scoped to the remote's origin so the header can't ride a redirect to
 * another host. Returns undefined for non-https remotes — a token header is
 * only meaningful over https; ssh/local remotes authenticate differently.
 *
 * The credential lives ONLY in the child's environment: never in argv (which
 * any process can read from /proc), never in the config file on disk, never
 * logged. Exported for unit tests.
 */
export function buildGitAuthEnv(
  remote: string,
  token: string,
): Record<string, string> | undefined {
  let origin: string;
  try {
    const url = new URL(remote);
    if (url.protocol !== 'https:') return undefined;
    origin = `${url.protocol}//${url.host}/`;
  } catch {
    return undefined;
  }
  const basic = Buffer.from(`x-access-token:${token}`).toString('base64');
  return {
    GIT_CONFIG_COUNT: '1',
    GIT_CONFIG_KEY_0: `http.${origin}.extraheader`,
    GIT_CONFIG_VALUE_0: `Authorization: Basic ${basic}`,
  };
}

// Full 40-hex only. `commit` is the integrity anchor, and an abbreviation
// isn't one: `cat-file -e <prefix>^{commit}` resolves a prefix to whatever
// object matches in the clone at that moment (ambiguous as the clone grows),
// and the direct-fetch fallback can't fetch an abbreviation at all. Real
// producers always send the full SHA (`snapshot.headSha`), so requiring it
// costs nothing and keeps the anchor honest.
const COMMIT_PATTERN = /^[0-9a-f]{40}$/i;
// Refs reach `git fetch` argv verbatim, so beyond plausibility this guards
// against option injection: nothing ref-like starts with `-`.
const REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9/_.@^~+-]*$/;

/**
 * Pull the `git` namespace out of the workspace bag and validate the shape
 * this executor is about to hand to argv. Shape errors degrade (with the
 * reason in the audit row) rather than throw: a malformed or foreign-
 * namespace workspace means "no trustworthy checkout", the same outcome as
 * a failed fetch.
 */
function readGitWorkspace(
  workspace: PromptExecutionWorkspace,
): { git: GitWorkspace } | { warning: string } {
  const git = workspace.git;
  if (git === undefined) {
    const offered = Object.keys(workspace).join(', ') || '(none)';
    return {
      warning: `workspace carries no namespace this executor understands (offered: ${offered}; supported: git)`,
    };
  }
  if (typeof git.remote !== 'string' || git.remote.length === 0 || git.remote.startsWith('-')) {
    return { warning: 'workspace.git.remote must be a non-empty remote URL or path' };
  }
  if (typeof git.commit !== 'string' || !COMMIT_PATTERN.test(git.commit)) {
    return { warning: 'workspace.git.commit must be a full 40-char commit SHA' };
  }
  if (git.fetchRefs !== undefined) {
    if (!Array.isArray(git.fetchRefs) || !git.fetchRefs.every((r) => typeof r === 'string')) {
      return { warning: 'workspace.git.fetchRefs must be an array of ref strings' };
    }
    const bad = git.fetchRefs.find((r) => !REF_PATTERN.test(r));
    if (bad !== undefined) {
      return { warning: `workspace.git.fetchRefs contains an invalid ref: ${JSON.stringify(bad)}` };
    }
  }
  return { git };
}

async function degradedWorkspace(
  warning: string,
  tmpBase: string = os.tmpdir(),
): Promise<PreparedWorkspace> {
  const scratchDir = await fs.mkdtemp(path.join(tmpBase, 'shuttle-ws-degraded-'));
  return {
    cwd: scratchDir,
    materialized: false,
    warning,
    cleanup: async () => {
      await fs.rm(scratchDir, { recursive: true, force: true }).catch(() => {});
    },
  };
}

interface GitResult {
  code: number;
  stdout: string;
  stderr: string;
}

/**
 * Run one git command, never throwing: spawn errors, timeouts, and aborts all
 * surface as a non-zero `code` with the cause in `stderr`, so callers can
 * treat every failure as "try the next source / degrade" without try/catch
 * pyramids. `GIT_TERMINAL_PROMPT=0` turns a would-be credential prompt into a
 * fast failure instead of a hang; `GIT_ALLOW_PROTOCOL` allowlists transports.
 *
 * k:env-boundary — the child git process needs the host environment (PATH,
 * HOME, ssh-agent, credential helpers) passed through; this is process
 * plumbing at the executor boundary, not package configuration.
 */
function runGit(
  args: string[],
  cwd: string,
  opts: {
    timeoutMs: number;
    signal?: AbortSignal;
    allowedProtocols: string;
    extraEnv?: Record<string, string>;
  },
): Promise<GitResult> {
  return new Promise((resolve) => {
    // Spawn failures (missing git binary, bad cwd) arrive via the 'error'
    // event, not a synchronous throw, so no try/catch is needed here.
    const child = spawn('git', args, {
      cwd,
      stdio: ['ignore', 'pipe', 'pipe'],
      // `extraEnv` (e.g. the credential extraheader) goes first so the two
      // security-critical vars below always win and can't be overridden.
      env: {
        ...process.env,
        ...(opts.extraEnv ?? {}),
        GIT_TERMINAL_PROMPT: '0',
        // Transport allowlist. `remote` passes readGitWorkspace as any
        // non-empty non-`-` string, which still admits `ext::sh -c …` and
        // `fd::` remotes that execute arbitrary commands on this host.
        // Restricting to the transports we actually use turns those into a
        // fast failure (→ degrade) rather than RCE across the boundary this
        // executor is meant to defend.
        GIT_ALLOW_PROTOCOL: opts.allowedProtocols,
      },
    });

    const stdout: Buffer[] = [];
    const stderr: Buffer[] = [];
    let settled = false;
    const settle = (result: GitResult): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };

    // SIGTERM first so git can drop its lock files (a SIGKILLed fetch can
    // leave stale locks that poison later fetches in the profile clone);
    // escalate only if it lingers.
    const killChild = (): void => {
      child.kill('SIGTERM');
      const escalate = setTimeout(() => child.kill('SIGKILL'), 5_000);
      escalate.unref();
      child.once('close', () => clearTimeout(escalate));
    };

    const timer = setTimeout(() => {
      killChild();
      settle({
        code: -1,
        stdout: Buffer.concat(stdout).toString('utf-8'),
        stderr: `git ${args[0]} timed out after ${opts.timeoutMs}ms`,
      });
    }, opts.timeoutMs);

    const onAbort = (): void => {
      killChild();
      settle({
        code: -1,
        stdout: Buffer.concat(stdout).toString('utf-8'),
        stderr: 'aborted',
      });
    };
    if (opts.signal?.aborted) {
      onAbort();
      return;
    }
    opts.signal?.addEventListener('abort', onAbort, { once: true });

    child.stdout!.on('data', (c: Buffer) => stdout.push(c));
    child.stderr!.on('data', (c: Buffer) => stderr.push(c));
    child.on('error', (err: Error) => settle({ code: -1, stdout: '', stderr: err.message }));
    child.on('close', (code: number | null) =>
      settle({
        code: code ?? -1,
        stdout: Buffer.concat(stdout).toString('utf-8'),
        stderr: Buffer.concat(stderr).toString('utf-8'),
      }),
    );
  });
}

function firstLine(text: string): string {
  const line = text.trim().split('\n', 1)[0];
  return line.length > 0 ? line : '(no output)';
}
